/**
 * 账号绑定设备优先，再拼托管 NVR（trust/device/list）；同一 serial+channel 去重
 * 说明：托管列表常含 channelNo=0 的「设备占位」行，无预览画面；真实摄像头多为 1 或 NVR 多路。此处跳过小于 1 的通道，避免占格/重复
 */

const normSerial = (s) => (s == null ? '' : String(s).trim().toUpperCase())

/**
 * @param {Array} accountList 账号下设备
 * @param {Array} trustList 托管设备列表
 * @returns {Array<{
 *   deviceSerial: string,
 *   channelNo: number,
 *   name: string,
 *   source: 'account'|'trust',
 *   trustCategory?: string,
 *   trustOnlineStatus?: * 托管条目的 status / onlineStatus（与 Nvr 表一致，用于在线/离线展示）
 *   accountDeviceStatus?: * 账号设备 lapp/device/list 的 status（0 离线, 1 在线, 2 休眠）
 * }>}
 * 托管：展示名优先 cameraName；分类键为 deviceName（无则「未分类」）
 */
/**
 * 与托管列表状态列：status，否则 onlineStatus
 */
export function getTrustDeviceOnlineInfo (value) {
  if (value === 1 || value === '1' || value === 'online') {
    return { isOnline: true, text: '在线' }
  }
  if (value === 0 || value === '0' || value === 'offline') {
    return { isOnline: false, text: '离线' }
  }
  return { isOnline: null, text: '—' }
}

/**
 * 设备状态：0 离线, 1 在线, 2 休眠
 * @returns {{ text: string, isOnline: boolean|null, tagColor: string }}
 */
export function getAccountDeviceStatusInfo (value) {
  if (value === 0 || value === '0') {
    return { text: '离线', isOnline: false, tagColor: 'error' }
  }
  if (value === 1 || value === '1') {
    return { text: '在线', isOnline: true, tagColor: 'success' }
  }
  if (value === 2 || value === '2') {
    return { text: '休眠', isOnline: null, tagColor: 'warning' }
  }
  return { text: '—', isOnline: null, tagColor: 'default' }
}

export function mergeAccountAndTrustDevices (accountList, trustList) {
  const seen = new Set()
  const out = []
  const push = (raw, source) => {
    const serial = normSerial(raw?.deviceSerial || raw?.device_serial)
    if (!serial) return
    const rawCh = raw?.channelNo ?? raw?.channel_no
    const ch =
      rawCh === undefined || rawCh === null || rawCh === ''
        ? 1
        : Number(rawCh)
    if (!Number.isFinite(ch) || ch < 1) return
    const key = `${serial}_${ch}`
    if (seen.has(key)) return
    seen.add(key)

    const cameraName = (raw?.cameraName || raw?.camera_name || '').trim()
    const devName = (raw?.deviceName || raw?.device_name || '').trim()

    let name
    let trustCategory
    if (source === 'trust') {
      const typeName = devName
      trustCategory = typeName || '未分类'
      name =
        cameraName ||
        (raw?.name && String(raw.name).trim()) ||
        (typeName ? `${typeName} · 通道${ch}` : `通道${ch} · ${serial}`)
    } else {
      name =
        cameraName ||
        devName ||
        (raw?.name && String(raw.name).trim()) ||
        serial
    }

    const row = {
      deviceSerial: serial,
      channelNo: ch,
      name,
      source
    }
    if (source === 'trust') {
      row.trustCategory = trustCategory
      const s = raw?.status
      const os = raw?.onlineStatus ?? raw?.online_status
      row.trustOnlineStatus = s != null && s !== '' ? s : os
    } else {
      if (raw?.status != null && raw?.status !== '') {
        row.accountDeviceStatus = raw.status
      }
    }
    out.push(row)
  }
  ;(accountList || []).forEach((d) => push(d, 'account'))
  ;(trustList || []).forEach((d) => push(d, 'trust'))
  return out
}

/** 店铺未分配占位键（筛选 / 分组共用） */
export const YS_DEVICE_UNASSIGNED_STORE_KEY = '__unassigned__'

/** 安装位置未设置占位键 */
export const YS_DEVICE_UNASSIGNED_LOCATION_KEY = '__unassigned_location__'

export function isDeviceStoreUnassigned (storeId) {
  if (storeId == null || storeId === '') return true
  const n = Number(storeId)
  if (!Number.isFinite(n) || n === 0) return true
  return false
}

/** 展示名：记录 storeName → Redux 店铺选项 → 空 */
export function resolveStoreDisplayName (record, storeNameById) {
  if (isDeviceStoreUnassigned(record?.storeId)) return ''
  const fromRecord = (record?.storeName || '').trim()
  if (fromRecord) return fromRecord
  const id = record?.storeId
  if (id == null || id === '') return ''
  const map = storeNameById instanceof Map ? storeNameById : new Map()
  return map.get(String(id)) || ''
}

function normalizeYingshiDeviceLocation (raw) {
  return raw != null ? String(raw).trim() : ''
}

/** @returns {{ locationKey: string, locationTitle: string, isUnassignedLocation: boolean }} */
export function resolveDeviceLocationGroup (record) {
  const label = normalizeYingshiDeviceLocation(record?.location)
  if (!label) {
    return {
      locationKey: YS_DEVICE_UNASSIGNED_LOCATION_KEY,
      locationTitle: '未设置位置',
      isUnassignedLocation: true
    }
  }
  return {
    locationKey: label,
    locationTitle: label,
    isUnassignedLocation: false
  }
}

function compareLocaleOptionalEmpty (a, b) {
  const left = String(a || '').trim()
  const right = String(b || '').trim()
  if (left !== right) {
    if (!left) return 1
    if (!right) return -1
    return left.localeCompare(right, 'zh-Hans-CN')
  }
  return 0
}

/**
 * 组内排序：安装位置 → 通道 → 品牌（空值靠后）→ 序列号
 * @param {Array} list 设备卡片行
 */
export function sortDevicesByChannel (list) {
  return [...(list || [])].sort((a, b) => {
    const locDiff = compareLocaleOptionalEmpty(a?.location, b?.location)
    if (locDiff !== 0) return locDiff
    const ac = Number(a?.channelNo) || 0
    const bc = Number(b?.channelNo) || 0
    if (ac !== bc) return ac - bc
    const brandDiff = compareLocaleOptionalEmpty(a?.brand, b?.brand)
    if (brandDiff !== 0) return brandDiff
    return String(a?.deviceSerial || '').localeCompare(String(b?.deviceSerial || ''), 'en')
  })
}

/** 排序档位：0 报警中 → 1 已开推理 → 2 未开推理 */
export const YS_DEVICE_ALARM_SORT_TIER = {
  ALARM: 0,
  INFERENCE: 1,
  NO_INFERENCE: 2
}

function compareYingshiDeviceChannelSerial (a, b) {
  const ac = Number(a?.channelNo) || 0
  const bc = Number(b?.channelNo) || 0
  if (ac !== bc) return ac - bc
  return String(a?.deviceSerial || '').localeCompare(String(b?.deviceSerial || ''), 'en')
}

/**
 * 单设备排序元数据（须与告警展示资格一致）
 * @param {Object} device
 * @param {{
 *   resolveActiveAlarm?: (device) => { levelValue?: number, eventTimeMs?: number } | null | undefined,
 *   isAlarmEligible?: (device) => boolean
 * }} [ctx]
 */
export function getYingshiDeviceAlarmSortMeta (device, ctx = {}) {
  const { resolveActiveAlarm, isAlarmEligible } = ctx
  if (device?.needInference !== true) {
    return { tier: YS_DEVICE_ALARM_SORT_TIER.NO_INFERENCE, levelValue: 0, eventTimeMs: 0 }
  }
  const eligible = typeof isAlarmEligible === 'function' ? isAlarmEligible(device) : true
  if (!eligible) {
    return { tier: YS_DEVICE_ALARM_SORT_TIER.INFERENCE, levelValue: 0, eventTimeMs: 0 }
  }

  const active = typeof resolveActiveAlarm === 'function' ? resolveActiveAlarm(device) : null
  if (active) {
    return {
      tier: YS_DEVICE_ALARM_SORT_TIER.ALARM,
      levelValue: Number(active.levelValue) || 0,
      eventTimeMs: Number(active.eventTimeMs) || 0
    }
  }

  if (device?.isAlarming === true) {
    const apiLevel = Number(device?.alarmLevel)
    return {
      tier: YS_DEVICE_ALARM_SORT_TIER.ALARM,
      levelValue: Number.isFinite(apiLevel) && apiLevel > 0 ? apiLevel : 0,
      eventTimeMs: Number(device?.alarmTimeMs) || 0
    }
  }

  return { tier: YS_DEVICE_ALARM_SORT_TIER.INFERENCE, levelValue: 0, eventTimeMs: 0 }
}

/**
 * 设备排序：报警等级高优先 → 已开推理居中 → 未开推理最后；同档内按通道/序列号
 * @param {Array} devices
 * @param {Parameters<typeof getYingshiDeviceAlarmSortMeta>[1]} [ctx]
 */
export function sortYingshiDevicesByAlarmAndInference (devices, ctx = {}) {
  return [...(devices || [])]
    .map((device, index) => ({ device, index }))
    .sort((a, b) => {
      const metaA = getYingshiDeviceAlarmSortMeta(a.device, ctx)
      const metaB = getYingshiDeviceAlarmSortMeta(b.device, ctx)
      if (metaA.tier !== metaB.tier) return metaA.tier - metaB.tier

      if (metaA.tier === YS_DEVICE_ALARM_SORT_TIER.ALARM) {
        // 等级高优先；同级再按已持续时长长→短（告警起点早的在前）
        const levelDiff = (metaB.levelValue || 0) - (metaA.levelValue || 0)
        if (levelDiff !== 0) return levelDiff
        const timeA = metaA.eventTimeMs || 0
        const timeB = metaB.eventTimeMs || 0
        if (timeA > 0 && timeB > 0 && timeA !== timeB) return timeA - timeB
        if (timeA > 0 && timeB <= 0) return -1
        if (timeB > 0 && timeA <= 0) return 1
      }

      const channelDiff = compareYingshiDeviceChannelSerial(a.device, b.device)
      if (channelDiff !== 0) return channelDiff

      return a.index - b.index
    })
    .map(({ device }) => device)
}

/**
 * 按来源拆分：账号一块；托管按 NVR（trustCategory）分组
 * @returns {{ accountItems: Array, trustGroups: Array<{ category: string, items: Array }> }}
 */
export function splitAccountAndTrustGroups (devices) {
  const account = []
  const byCat = new Map()
  for (const d of devices || []) {
    if (d.source === 'account') {
      account.push(d)
      continue
    }
    const cat = d.trustCategory || '未分类'
    if (!byCat.has(cat)) {
      byCat.set(cat, [])
    }
    byCat.get(cat).push(d)
  }
  const keys = [...byCat.keys()].sort((a, b) => a.localeCompare(b, 'zh-Hans-CN'))
  const trustGroups = keys.map((k) => ({
    category: k,
    items: sortDevicesByChannel(byCat.get(k) || [])
  }))
  return {
    accountItems: sortDevicesByChannel(account),
    trustGroups
  }
}

/**
 * 账号 / 托管 NVR 子分区（纯数据，标题由页面渲染）
 * @returns {Array<{ sectionKey: string, sectionKind: 'account'|'trust', category?: string, title: string, items: Array }>}
 */
export function buildYingshiDeviceSourceSections (devices) {
  const { accountItems, trustGroups } = splitAccountAndTrustGroups(devices)
  const sections = []
  if (accountItems.length > 0) {
    sections.push({
      sectionKey: 'account',
      sectionKind: 'account',
      title: '账号设备',
      items: accountItems
    })
  }
  for (const group of trustGroups) {
    sections.push({
      sectionKey: `trust-${group.category}`,
      sectionKind: 'trust',
      category: group.category,
      title: `托管 · ${group.category}`,
      items: group.items
    })
  }
  return sections
}

/**
 * 设备列表：组内按安装位置（location）分区，同位置多摄像头同网格展示
 * @returns {Array<{ sectionKey: string, sectionKind: 'location', location: string, title: string, isUnassignedLocation: boolean, items: Array }>}
 */
export function buildYingshiDeviceLocationSections (devices, options = {}) {
  const { preserveItemOrder = false } = options
  const byLocation = new Map()
  for (const d of devices || []) {
    const { locationKey, locationTitle, isUnassignedLocation } = resolveDeviceLocationGroup(d)
    if (!byLocation.has(locationKey)) {
      byLocation.set(locationKey, {
        locationKey,
        locationTitle,
        isUnassignedLocation,
        items: []
      })
    }
    byLocation.get(locationKey).items.push(d)
  }

  const sorted = [...byLocation.values()].sort((a, b) => {
    if (a.isUnassignedLocation !== b.isUnassignedLocation) {
      return a.isUnassignedLocation ? 1 : -1
    }
    return String(a.locationTitle).localeCompare(String(b.locationTitle), 'zh-Hans-CN')
  })

  return sorted.map((bucket) => ({
    sectionKey: `location-${bucket.locationKey}`,
    sectionKind: 'location',
    location: bucket.locationTitle,
    title: bucket.locationTitle,
    isUnassignedLocation: bucket.isUnassignedLocation,
    items: preserveItemOrder ? bucket.items : sortDevicesByChannel(bucket.items)
  }))
}

/**
 * 设备列表 / 店铺分配：未筛店时先按店铺，组内再账号 / 托管 NVR
 * @param {Map<string, string>} storeNameById
 * @param {{ useLocationSections?: boolean }} [options] 设备列表传 true：组内按 location
 * @returns {Array<{ groupKey: string, groupTitle: string, isUnassigned: boolean, sections: Array }>}
 */
export function buildYingshiDeviceStoreTopGroups (devices, storeNameById, options = {}) {
  const { useLocationSections = false, preserveItemOrder = false } = options
  const buildSections = useLocationSections
    ? (devs) => buildYingshiDeviceLocationSections(devs, { preserveItemOrder })
    : buildYingshiDeviceSourceSections
  const byStore = new Map()
  for (const d of devices || []) {
    const unassigned = isDeviceStoreUnassigned(d.storeId)
    const storeKey = unassigned ? YS_DEVICE_UNASSIGNED_STORE_KEY : String(d.storeId)
    let storeTitle = '待分配'
    if (!unassigned) {
      storeTitle = resolveStoreDisplayName(d, storeNameById) || `店铺#${d.storeId}`
    }
    if (!byStore.has(storeKey)) {
      byStore.set(storeKey, {
        groupKey: storeKey,
        groupTitle: storeTitle,
        isUnassigned: unassigned,
        devices: []
      })
    }
    byStore.get(storeKey).devices.push(d)
  }

  const sorted = [...byStore.values()].sort((a, b) => {
    if (a.groupKey === YS_DEVICE_UNASSIGNED_STORE_KEY) return -1
    if (b.groupKey === YS_DEVICE_UNASSIGNED_STORE_KEY) return 1
    return String(a.groupTitle).localeCompare(String(b.groupTitle), 'zh-Hans-CN')
  })

  return sorted.map((bucket) => ({
    groupKey: bucket.groupKey,
    groupTitle: bucket.groupTitle,
    isUnassigned: bucket.isUnassigned,
    sections: buildSections(bucket.devices)
  }))
}

/**
 * 设备列表展示分组：店铺 → location；筛店后仅 location
 * @param {{ devices: Array, filterStoreId?: string, storeNameById?: Map<string, string> }} opts
 */
export function buildYingshiDeviceDisplayGroups ({ devices, filterStoreId, storeNameById, preserveItemOrder = false }) {
  const rows = devices || []
  if (!rows.length) return []
  if (!filterStoreId) {
    return buildYingshiDeviceStoreTopGroups(rows, storeNameById, {
      useLocationSections: true,
      preserveItemOrder
    })
  }
  const sections = buildYingshiDeviceLocationSections(rows, { preserveItemOrder })
  if (!sections.length) return []
  return [
    {
      groupKey: 'filtered',
      groupTitle: '',
      isUnassigned: filterStoreId === YS_DEVICE_UNASSIGNED_STORE_KEY,
      sections
    }
  ]
}

/** 先账号块（内按通道），再托管块（内按通道），与合并语义一致 */
export function sortMergedAccountThenTrustByChannel (merged) {
  const acc = sortDevicesByChannel((merged || []).filter((d) => d.source === 'account'))
  const tru = sortDevicesByChannel((merged || []).filter((d) => d.source === 'trust'))
  return [...acc, ...tru]
}

/**
 * @typedef {Object} YingshiDeviceVO
 * @property {number} [id]
 * @property {string} [serial]
 * @property {number} [channelNo]
 * @property {string} [name]
 * @property {number} [status] 是否在线：1 在线 / 0 离线
 * @property {string} [statusDesc]
 * @property {string} [location]
 * @property {number} [sort]
 * @property {boolean|number} [isPinned]
 * @property {boolean|number} [isTrustDevice]
 * @property {boolean|number} [needInference]
 * @property {boolean} [isAlarming] 是否告警中（设备同层）
 * @property {Object} [latestMonitoringMessage] 该摄像头最新一条监控记录
 * @property {number|string} [storeId]
 * @property {number|string} [store_id]
 * @property {string} [storeName]
 * @property {string} [store_name]
 * @property {string} [brand] 设备品牌；仅「萤石」走萤石云抓拍
 * @property {string|number} [bedNo] 床位号
 */

function parseNeedInference (raw) {
  if (raw === true || raw === 1 || raw === '1') return true
  if (raw === false || raw === 0 || raw === '0') return false
  return false
}

/** latestMonitoringMessage.hasBaby：仅明确布尔可解析时写入；缺省返回 undefined（不默认 false） */
function parseHasBaby (raw) {
  if (raw === true || raw === 1 || raw === '1') return true
  if (raw === false || raw === 0 || raw === '0') return false
  if (typeof raw === 'string') {
    const s = raw.trim().toLowerCase()
    if (s === 'true') return true
    if (s === 'false') return false
  }
  return undefined
}

function parseIsAlarming (raw) {
  if (raw === true || raw === 1 || raw === '1') return true
  if (raw === false || raw === 0 || raw === '0') return false
  return false
}

/**
 * list.alarmLevel：可为数字，或对象 { value, desc?, name? }（以 value 为等级）
 * @returns {{ levelValue: number, levelText: string }}
 */
function parseAlarmLevel (raw) {
  if (raw == null || raw === '') return { levelValue: 0, levelText: '' }
  if (typeof raw === 'object' && !Array.isArray(raw)) {
    const levelValue = Number(raw.value ?? raw.level)
    const safeLevel = Number.isFinite(levelValue) && levelValue > 0 ? levelValue : 0
    let levelText = ''
    if (raw.desc != null && String(raw.desc).trim() !== '') {
      levelText = String(raw.desc).trim()
    } else if (raw.name != null && String(raw.name).trim() !== '') {
      levelText = String(raw.name).trim()
    }
    return { levelValue: safeLevel, levelText }
  }
  const n = Number(raw)
  if (!Number.isFinite(n) || n <= 0) return { levelValue: 0, levelText: '' }
  return { levelValue: n, levelText: '' }
}

/** 告警时间 → 毫秒；无法解析返回 0 */
function parseAlarmTimeMs (raw) {
  if (raw == null || raw === '') return 0
  if (typeof raw === 'number' && Number.isFinite(raw) && raw > 0) {
    return raw < 1e12 ? raw * 1000 : raw
  }
  const n = Number(raw)
  if (Number.isFinite(n) && n > 0) return n < 1e12 ? n * 1000 : n
  const t = Date.parse(String(raw))
  return Number.isNaN(t) ? 0 : t
}

function getLatestMonitoringMessage (vo) {
  const a = vo?.latestMonitoringMessage
  if (!a || typeof a !== 'object' || Array.isArray(a)) return null
  return a
}

function firstUrlInAlarmArray (arr) {
  if (!Array.isArray(arr) || arr.length === 0) return ''
  const hit = arr.find((u) => typeof u === 'string' && u.trim() !== '')
  return hit ? hit.trim() : ''
}

function trimMonitoringImage (raw) {
  return raw != null ? String(raw).trim() : ''
}

/**
 * 从 list 行解析告警展示字段：详情在 latestMonitoringMessage，isAlarming 在设备同层。
 */
function mapAlarmFieldsFromDeviceVo (vo) {
  const message = getLatestMonitoringMessage(vo)
  const isAlarming = parseIsAlarming(vo?.isAlarming ?? vo?.is_alarming)
  const hasBaby = parseHasBaby(message?.hasBaby)
  const { levelValue, levelText } = parseAlarmLevel(
    message?.alarmLevel ?? message?.level ?? vo?.alarmLevel ?? vo?.alarm_level
  )
  const thumb = trimMonitoringImage(
    message?.thumbnailUrl
    || message?.thumbnail_url
    || firstUrlInAlarmArray(message?.thumbnailUrls)
    || firstUrlInAlarmArray(message?.thumbnail_urls)
    || firstUrlInAlarmArray(message?.imageUrls)
    || firstUrlInAlarmArray(message?.image_urls)
  )
  const full = trimMonitoringImage(
    message?.imageUrl
    || message?.image_url
    || firstUrlInAlarmArray(message?.imageUrls)
    || firstUrlInAlarmArray(message?.image_urls)
    || firstUrlInAlarmArray(message?.thumbnailUrls)
    || firstUrlInAlarmArray(message?.thumbnail_urls)
    || thumb
  )
  const alarmTimeMs = parseAlarmTimeMs(vo?.alarmTime)
  const summaryRaw = message?.summary ?? message?.alarmSummary ?? message?.message
  const alarmSummary = summaryRaw != null ? String(summaryRaw).trim() : ''
  return {
    latestMonitoringMessage: message,
    isAlarming,
    hasBaby,
    alarmLevel: levelValue,
    alarmLevelText: levelText,
    latestMonitoringImageThumbnail: thumb,
    latestMonitoringImage: full || thumb,
    alarmTimeMs,
    alarmSummary
  }
}

/** Java 列表：缺省通道 1；单机可为 0；仅跳过非法或负数 */
function normalizeYingshiChannelNo (rawCh) {
  if (rawCh === undefined || rawCh === null || rawCh === '') return 1
  const ch = Number(rawCh)
  if (!Number.isFinite(ch) || ch < 0) return null
  return ch
}

/**
 * Java GET /monitoringdevice/list → 设备卡片行；账号优先去重。
 * 托管分组键 trustCategory = serial（父设备/NVR 序列号），同父设备多通道归入同一组。
 * 先展开 list 行，再覆盖需规范化的字段；未改动的接口字段（如 bedNo）原样保留。
 * @param {YingshiDeviceVO[]} list
 */
export function mapYingshiDevicesToMergedList (list) {
  const raw = Array.isArray(list) ? list : []
  const sorted = [...raw].sort((a, b) => {
    const ap = a?.isPinned === true || a?.isPinned === 1 ? 1 : 0
    const bp = b?.isPinned === true || b?.isPinned === 1 ? 1 : 0
    if (ap !== bp) return bp - ap
    const as = Number(a?.sort)
    const bs = Number(b?.sort)
    if (Number.isFinite(as) && Number.isFinite(bs) && as !== bs) return as - bs
    return 0
  })

  const seen = new Set()
  const out = []
  const ordered = [
    ...sorted.filter((d) => !(d?.isTrustDevice === true || d?.isTrustDevice === 1)),
    ...sorted.filter((d) => d?.isTrustDevice === true || d?.isTrustDevice === 1)
  ]

  for (const vo of ordered) {
    const serial = normSerial(
      vo?.serial ?? vo?.deviceSerial ?? vo?.device_serial ?? vo?.device_sn
    )
    if (!serial) continue
    const rawCh = vo?.channelNo ?? vo?.channel_no ?? vo?.channel
    const ch = normalizeYingshiChannelNo(rawCh)
    if (ch == null) continue
    const key = `${serial}_${ch}`
    if (seen.has(key)) continue
    seen.add(key)

    const isTrust = vo?.isTrustDevice === true || vo?.isTrustDevice === 1
    const name = (
      (vo?.name && String(vo.name).trim()) ||
      (vo?.deviceName && String(vo.deviceName).trim()) ||
      (vo?.cameraName && String(vo.cameraName).trim()) ||
      serial
    )
    const storeIdRaw = vo?.storeId ?? vo?.store_id
    const storeNameRaw = vo?.storeName ?? vo?.store_name
    const brandRaw = vo?.brand
    const alarmFields = mapAlarmFieldsFromDeviceVo(vo)
    const deviceFields = { ...vo }
    delete deviceFields.latestAlarm
    delete deviceFields.latest_alarm
    const row = {
      ...deviceFields,
      deviceSerial: serial,
      channelNo: ch,
      name,
      source: isTrust ? 'trust' : 'account',
      needInference: parseNeedInference(vo?.needInference),
      storeId: storeIdRaw == null || storeIdRaw === '' ? null : storeIdRaw,
      storeName: storeNameRaw != null ? String(storeNameRaw).trim() : '',
      brand: brandRaw != null ? String(brandRaw).trim() : '',
      location: normalizeYingshiDeviceLocation(vo?.location),
      latestMonitoringMessage: alarmFields.latestMonitoringMessage,
      hasBaby: alarmFields.hasBaby,
      isAlarming: alarmFields.isAlarming,
      alarmLevel: alarmFields.alarmLevel,
      latestMonitoringImage: alarmFields.latestMonitoringImage,
      latestMonitoringImageThumbnail: alarmFields.latestMonitoringImageThumbnail,
      alarmTimeMs: alarmFields.alarmTimeMs,
      latestDepartureRecord: vo?.latestDepartureRecord ?? vo?.latest_departure_record ?? null
    }
    if (alarmFields.alarmLevelText) row.alarmLevelText = alarmFields.alarmLevelText
    if (alarmFields.alarmSummary) row.alarmSummary = alarmFields.alarmSummary
    if (isTrust) {
      row.trustCategory = serial
    }
    out.push(row)
  }
  return out
}
